import { UserContext } from '@providers/userContextProvider';
import { getPalette, type Hexcode } from 'hephaestus/core';
import { useContext, useMemo } from 'react';

/** Pre-hephaestus chart palette, used when an org sets no support colours. */
export type ChartSupportColorKey =
  | 'supportOne'
  | 'supportTwo'
  | 'supportThree'
  | 'supportFour'
  | 'supportFive'
  | 'supportSix'
  | 'supportSeven'
  | 'supportEight'
  | 'supportNine'
  | 'supportTen'
  | 'supportEleven'
  | 'supportTwelve'
  | 'supportThirteen'
  | 'supportFourteen'
  | 'supportFifteen';

export const CHART_COLOR_BY_SUPPORT: Record<ChartSupportColorKey, Hexcode> = {
  supportOne: '#297AA3',
  supportTwo: '#70BBC2',
  supportThree: '#F0BD42',
  supportFour: '#E27F36',
  supportFive: '#D65C85',
  supportSix: '#B670C2',
  /** Hephaestus `supportSeven50` duplicates slot six, so this slot needs its own hex. */
  supportSeven: '#394DAC',
  supportEight: '#8A6843',
  supportNine: '#7D7D7D',
  supportTen: '#C4C4C4',
  supportEleven: '#12873D',
  supportTwelve: '#DC2626',
  supportThirteen: '#24306B',
  supportFourteen: '#9E2E53',
  supportFifteen: '#007681',
};

/**
 * Slots that every asset class consumer can render, which keeps one asset class
 * on one colour across the donut, its legend, and the table chip beside it.
 *
 * Seven design system colours, because a chip needs a light background and a
 * readable foreground and the palette only defines the `10`/`50`/`60` triple up
 * to `supportSeven`. An org may hold far more asset classes than that, so these
 * repeat down the registry; what stops two of them landing on one screen in the
 * same colour is `buildInViewAssetClassColorMap`. See `AssetClassColor`.
 */
export const ASSET_CLASS_COLOR_KEYS = [
  'supportOne',
  'supportTwo',
  'supportThree',
  'supportFour',
  'supportFive',
  'supportSix',
  'supportSeven',
] as const;

export type AssetClassColorKey = (typeof ASSET_CLASS_COLOR_KEYS)[number];

/**
 * A slot, and how many times one screen has had to spend the palette to reach
 * this asset class. Shade `0` is the design system colour itself and is what
 * nearly everything renders at; a screen only reaches for a higher one once it
 * holds more distinct asset classes than the palette has colours, where the
 * alternative is showing two of them the same.
 */
export type AssetClassColor = {
  slot: AssetClassColorKey;
  shade: number;
};

export const DEFAULT_ASSET_CLASS_COLOR: AssetClassColor = {
  slot: 'supportOne',
  shade: 0,
};

/** Both chip colours, so a nudged slot keeps a readable foreground. */
export type AssetClassChipColor = {
  backgroundColor: Hexcode;
  textColor: Hexcode;
};

const CHANNEL_COUNT = 3;
const CHANNEL_STEP = 34;

/**
 * Moves one channel per shade, rotating which one and widening the step every
 * full turn, so each shade of a slot is a different colour and no two shades
 * collide. Always steps towards mid grey, which keeps a light chip background
 * light and a solid slice solid instead of clipping to white or black.
 */
export const shadeHex = (hex: Hexcode, shade: number): Hexcode => {
  if (shade <= 0) {
    return hex;
  }

  const channel = (shade - 1) % CHANNEL_COUNT;
  const step = Math.ceil(shade / CHANNEL_COUNT) * CHANNEL_STEP;
  const value = hex.replace('#', '');
  const full =
    value.length === 3
      ? value
          .split('')
          .map((part) => part + part)
          .join('')
      : value;

  const shifted = [0, 2, 4]
    .map((offset, index) => {
      const current = Number.parseInt(full.slice(offset, offset + 2), 16);
      if (index !== channel) {
        return current;
      }
      const towardsMid = current > 127 ? -step : step;
      return Math.min(255, Math.max(0, current + towardsMid));
    })
    .map((part) => part.toString(16).padStart(2, '0'))
    .join('');

  return `#${shifted}` as Hexcode;
};

/** The identifying fields an asset class carries, from any of our queries. */
export type AssetClassColorSource = {
  id?: string | null;
  key?: string | null;
  name?: string | null;
};

const trimmed = (value: string | null | undefined): string | undefined =>
  value?.trim() || undefined;

/** A row carries this id only until a security is picked for it. */
const TEMP_ID_PREFIX = 'temp-';

/**
 * Every name an asset class can be recognised by. Colours are looked up against
 * all of them because rows built in the browser before saving (the cash
 * placeholder) carry only a temporary id and a label, while saved rows carry a
 * `key` — matching on any one alias keeps both on the same colour.
 */
export const assetClassAliases = (
  assetClass: AssetClassColorSource | null | undefined,
): string[] => {
  if (!assetClass) {
    return [];
  }

  return [
    trimmed(assetClass.key),
    trimmed(assetClass.id),
    trimmed(assetClass.name)?.toLowerCase(),
  ].filter((alias): alias is string => Boolean(alias));
};

/**
 * The single alias an asset class is grouped by on a screen, chosen so that the
 * choice never depends on which fields a query happened to select.
 *
 * Our queries disagree on that: the household allocation asks for `id`, `name`
 * and `key`, while `FETCH_MODEL_PORTFOLIO` behind the admin page asks for `id`
 * and `name` alone. Reading whichever alias came first therefore read one asset
 * class as two, and had them contest a colour with themselves. `id` is the
 * field every colour-bearing query selects, so it decides; a row still holding
 * a placeholder id falls through to the fields it will share with its saved
 * self.
 */
export const canonicalAssetClassAlias = (
  assetClass: AssetClassColorSource | null | undefined,
): string | undefined => {
  if (!assetClass) {
    return undefined;
  }

  const id = trimmed(assetClass.id);

  return (
    (id?.startsWith(TEMP_ID_PREFIX) ? undefined : id) ??
    trimmed(assetClass.key) ??
    trimmed(assetClass.name)?.toLowerCase()
  );
};

/**
 * Assigns one colour per asset class from its position in `assetClasses`, which
 * must be the organization's full list rather than the subset held by whichever
 * portfolio is on screen. Colouring by position within a portfolio is what made
 * the same asset class change colour between models.
 *
 * Always a design system colour, never a shade of one. Numbering shades from
 * here would reach the eleventh for an org holding eighty asset classes, and by
 * then the colour is a magenta the palette never defined. A screen holds a
 * handful, so `buildInViewAssetClassColorMap` spends shades only where that
 * handful actually clashes, which keeps all but the crowded screens on colours
 * the design system chose.
 */
export const buildAssetClassColorMap = (
  assetClasses: readonly AssetClassColorSource[],
): Record<string, AssetClassColor> => {
  const colorByAlias: Record<string, AssetClassColor> = {};

  assetClasses.forEach((assetClass, index) => {
    const color = {
      slot: ASSET_CLASS_COLOR_KEYS[index % ASSET_CLASS_COLOR_KEYS.length],
      shade: 0,
    };
    for (const alias of assetClassAliases(assetClass)) {
      // An earlier asset class wins a shared alias, so its colour never moves.
      if (!(alias in colorByAlias)) {
        colorByAlias[alias] = color;
      }
    }
  });

  return colorByAlias;
};

/**
 * What the registry leaves out is narrow: a class archived since a security
 * first referenced it, one past the page we ask for, or a set from a
 * passthrough API. Those take the default, and `buildInViewAssetClassColorMap`
 * moves them off it if they ever share a screen with the class that owns it.
 */
export const resolveAssetClassColorFromMap = (
  colorByAlias: Record<string, AssetClassColor>,
  assetClass: AssetClassColorSource | null | undefined,
): AssetClassColor => {
  for (const alias of assetClassAliases(assetClass)) {
    const color = colorByAlias[alias];
    if (color) {
      return color;
    }
  }

  return DEFAULT_ASSET_CLASS_COLOR;
};

/**
 * Colours for the asset classes on one screen. Each keeps its registry colour
 * where it can, and where two of them want the same one — unavoidable while an
 * org holds more asset classes than the palette has slots — the loser moves to
 * a colour no asset class on that screen is using. Past the seventh that means
 * a nudged shade of a slot rather than the slot itself, so a screen can hold
 * more asset classes than the palette has colours and keep them all apart.
 *
 * Assignment depends only on *which* asset classes are on screen, never on the
 * order they arrive in, so the donut and the table beside it reach the same
 * answer without having to sort their rows alike. Both must pass the same set:
 * each derives it from the securities they share.
 *
 * Asset classes outside the set, such as a template target holding no
 * securities, keep their plain registry colour.
 */
type InViewAssetClass = { aliases: string[]; base: AssetClassColor };

/**
 * Every alias of a registered asset class pointing at one name for it, so the
 * same class arriving with different fields populated is not read as two
 * classes competing for a colour.
 */
const buildCanonicalAliasIndex = (
  assetClasses: readonly AssetClassColorSource[],
): Record<string, string> => {
  const canonicalByAlias: Record<string, string> = {};

  for (const assetClass of assetClasses) {
    const canonical = canonicalAssetClassAlias(assetClass);
    if (!canonical) {
      continue;
    }

    for (const alias of assetClassAliases(assetClass)) {
      if (!(alias in canonicalByAlias)) {
        canonicalByAlias[alias] = canonical;
      }
    }
  }

  return canonicalByAlias;
};

/** The distinct asset classes on one screen, each with the colour it starts from. */
const groupAssetClassesInView = (
  assetClassesInView: readonly (AssetClassColorSource | null | undefined)[],
  canonicalByAlias: Record<string, string>,
  colorByAlias: Record<string, AssetClassColor>,
): Map<string, InViewAssetClass> => {
  const inView = new Map<string, InViewAssetClass>();

  for (const assetClass of assetClassesInView) {
    const aliases = assetClassAliases(assetClass);
    if (aliases.length === 0) {
      continue;
    }

    // An unregistered class names itself, which is why that name cannot depend
    // on the fields whichever query supplied it chose to select.
    const identity =
      aliases.map((alias) => canonicalByAlias[alias]).find(Boolean) ??
      canonicalAssetClassAlias(assetClass) ??
      aliases[0];
    const seen = inView.get(identity);

    if (!seen) {
      inView.set(identity, {
        aliases: [...aliases],
        base: resolveAssetClassColorFromMap(colorByAlias, assetClass),
      });
      continue;
    }

    for (const alias of aliases) {
      if (!seen.aliases.includes(alias)) {
        seen.aliases.push(alias);
      }
    }
  }

  return inView;
};

/**
 * One slot each, keeping the colour an asset class came in with wherever two do
 * not want the same one. Sorted by identity so the result turns on which asset
 * classes are present and not the order they were listed in.
 */
const colorId = ({ slot, shade }: AssetClassColor): string =>
  `${slot}:${shade}`;

/** The design system colours first, then a shade at a time across all of them. */
const nextFreeColor = (taken: Set<string>): AssetClassColor => {
  for (let shade = 0; ; shade += 1) {
    const free = ASSET_CLASS_COLOR_KEYS.find(
      (slot) => !taken.has(colorId({ slot, shade })),
    );
    if (free) {
      return { slot: free, shade };
    }
  }
};

const assignDistinctSlots = (
  inView: Map<string, InViewAssetClass>,
): Map<string, AssetClassColor> => {
  const entries = [...inView.entries()].sort(([a], [b]) => a.localeCompare(b));
  const taken = new Set<string>();
  const assigned = new Map<string, AssetClassColor>();

  for (const [identity, { base }] of entries) {
    if (!taken.has(colorId(base))) {
      taken.add(colorId(base));
      assigned.set(identity, base);
    }
  }

  for (const [identity] of entries) {
    if (assigned.has(identity)) {
      continue;
    }

    const free = nextFreeColor(taken);
    taken.add(colorId(free));
    assigned.set(identity, free);
  }

  return assigned;
};

export const buildInViewAssetClassColorMap = (
  assetClasses: readonly AssetClassColorSource[],
  assetClassesInView: readonly (AssetClassColorSource | null | undefined)[],
): Record<string, AssetClassColor> => {
  const colorByAlias = buildAssetClassColorMap(assetClasses);
  const inView = groupAssetClassesInView(
    assetClassesInView,
    buildCanonicalAliasIndex(assetClasses),
    colorByAlias,
  );

  const colorByAliasInView = { ...colorByAlias };
  for (const [identity, slot] of assignDistinctSlots(inView)) {
    for (const alias of inView.get(identity)?.aliases ?? []) {
      colorByAliasInView[alias] = slot;
    }
  }

  return colorByAliasInView;
};

/** Raw org overrides: `theme.palette` is always filled with hephaestus defaults. */
const useOrgPalette = () => {
  const { activeOrganization } = useContext(UserContext);

  return activeOrganization?.themeTokens?.ref?.palette;
};

export const useChartColorBySupport = (): Record<
  ChartSupportColorKey,
  Hexcode
> => {
  const palette = useOrgPalette();

  return useMemo(
    () => ({
      supportOne: palette?.supportOne50 ?? CHART_COLOR_BY_SUPPORT.supportOne,
      supportTwo: palette?.supportTwo50 ?? CHART_COLOR_BY_SUPPORT.supportTwo,
      supportThree:
        palette?.supportThree50 ?? CHART_COLOR_BY_SUPPORT.supportThree,
      supportFour: palette?.supportFour50 ?? CHART_COLOR_BY_SUPPORT.supportFour,
      supportFive: palette?.supportFive50 ?? CHART_COLOR_BY_SUPPORT.supportFive,
      supportSix: palette?.supportSix50 ?? CHART_COLOR_BY_SUPPORT.supportSix,
      supportSeven:
        palette?.supportSeven50 ?? CHART_COLOR_BY_SUPPORT.supportSeven,
      supportEight:
        palette?.supportEight50 ?? CHART_COLOR_BY_SUPPORT.supportEight,
      supportNine: palette?.supportNine50 ?? CHART_COLOR_BY_SUPPORT.supportNine,
      supportTen: palette?.supportTen50 ?? CHART_COLOR_BY_SUPPORT.supportTen,
      supportEleven:
        palette?.supportEleven50 ?? CHART_COLOR_BY_SUPPORT.supportEleven,
      supportTwelve:
        palette?.supportTwelve50 ?? CHART_COLOR_BY_SUPPORT.supportTwelve,
      supportThirteen:
        palette?.supportThirteen50 ?? CHART_COLOR_BY_SUPPORT.supportThirteen,
      supportFourteen:
        palette?.supportFourteen50 ?? CHART_COLOR_BY_SUPPORT.supportFourteen,
      supportFifteen:
        palette?.supportFifteen50 ?? CHART_COLOR_BY_SUPPORT.supportFifteen,
    }),
    [
      palette?.supportOne50,
      palette?.supportTwo50,
      palette?.supportThree50,
      palette?.supportFour50,
      palette?.supportFive50,
      palette?.supportSix50,
      palette?.supportSeven50,
      palette?.supportEight50,
      palette?.supportNine50,
      palette?.supportTen50,
      palette?.supportEleven50,
      palette?.supportTwelve50,
      palette?.supportThirteen50,
      palette?.supportFourteen50,
      palette?.supportFifteen50,
    ],
  );
};

/**
 * Hephaestus slot ramp, read at module load because the defaults never change.
 */
const HEPHAESTUS_PALETTE = getPalette();

export type AssetClassColorRamp = {
  /** `supportN50`, for slices and legend dots. */
  solid: Record<AssetClassColorKey, Hexcode>;
  /** `supportN10`, for template targets that hold no securities yet. */
  light: Record<AssetClassColorKey, Hexcode>;
  /** `supportN60`, the foreground the chip pairs with `light`. */
  onLight: Record<AssetClassColorKey, Hexcode>;
};

/**
 * Hexes for the six asset class slots, taken from the hephaestus ramp rather
 * than `CHART_COLOR_BY_SUPPORT`. The `Badge` asset class chip renders slot N as
 * `supportN10`, and the two ramps disagree on which hue each slot holds: slot
 * four is orange in the chart ramp and blue in hephaestus. Resolving an asset
 * class to a stable slot was therefore not enough on its own, because the
 * donut and the chip beside it turned that same slot into different colours.
 */
export const useAssetClassColorRamp = (): AssetClassColorRamp => {
  const palette = useOrgPalette();

  return useMemo(
    () => ({
      solid: {
        supportOne: palette?.supportOne50 ?? HEPHAESTUS_PALETTE.supportOne50,
        supportTwo: palette?.supportTwo50 ?? HEPHAESTUS_PALETTE.supportTwo50,
        supportThree:
          palette?.supportThree50 ?? HEPHAESTUS_PALETTE.supportThree50,
        supportFour: palette?.supportFour50 ?? HEPHAESTUS_PALETTE.supportFour50,
        supportFive: palette?.supportFive50 ?? HEPHAESTUS_PALETTE.supportFive50,
        supportSix: palette?.supportSix50 ?? HEPHAESTUS_PALETTE.supportSix50,
        supportSeven:
          palette?.supportSeven50 ?? HEPHAESTUS_PALETTE.supportSeven50,
      },
      light: {
        supportOne: palette?.supportOne10 ?? HEPHAESTUS_PALETTE.supportOne10,
        supportTwo: palette?.supportTwo10 ?? HEPHAESTUS_PALETTE.supportTwo10,
        supportThree:
          palette?.supportThree10 ?? HEPHAESTUS_PALETTE.supportThree10,
        supportFour: palette?.supportFour10 ?? HEPHAESTUS_PALETTE.supportFour10,
        supportFive: palette?.supportFive10 ?? HEPHAESTUS_PALETTE.supportFive10,
        supportSix: palette?.supportSix10 ?? HEPHAESTUS_PALETTE.supportSix10,
        supportSeven:
          palette?.supportSeven10 ?? HEPHAESTUS_PALETTE.supportSeven10,
      },
      onLight: {
        supportOne: palette?.supportOne60 ?? HEPHAESTUS_PALETTE.supportOne60,
        supportTwo: palette?.supportTwo60 ?? HEPHAESTUS_PALETTE.supportTwo60,
        supportThree:
          palette?.supportThree60 ?? HEPHAESTUS_PALETTE.supportThree60,
        supportFour: palette?.supportFour60 ?? HEPHAESTUS_PALETTE.supportFour60,
        supportFive: palette?.supportFive60 ?? HEPHAESTUS_PALETTE.supportFive60,
        supportSix: palette?.supportSix60 ?? HEPHAESTUS_PALETTE.supportSix60,
        supportSeven:
          palette?.supportSeven60 ?? HEPHAESTUS_PALETTE.supportSeven60,
      },
    }),
    [palette],
  );
};

/** The slice or legend dot colour, nudged if the palette had to be reused. */
export const assetClassSolidColor = (
  ramp: AssetClassColorRamp,
  { slot, shade }: AssetClassColor,
): Hexcode => shadeHex(ramp.solid[slot], shade);

/** The template target colour, nudged if the palette had to be reused. */
export const assetClassLightColor = (
  ramp: AssetClassColorRamp,
  { slot, shade }: AssetClassColor,
): Hexcode => shadeHex(ramp.light[slot], shade);

/**
 * The pair a chip needs when its colour is past the design system, and
 * `undefined` when it is not — a plain slot renders from the `Badge` token so
 * the common case stays on design system colours rather than ones we mixed.
 *
 * Only the background moves. Holding the foreground at `supportN60` keeps the
 * label against a background that is still light, rather than letting two
 * shifts compound into something unreadable.
 */
export const assetClassChipColor = (
  ramp: AssetClassColorRamp,
  { slot, shade }: AssetClassColor,
): AssetClassChipColor | undefined =>
  shade === 0
    ? undefined
    : {
        backgroundColor: shadeHex(ramp.light[slot], shade),
        textColor: ramp.onLight[slot],
      };

export const useChartColors = (): Hexcode[] => {
  const colorBySupport = useChartColorBySupport();

  return useMemo(() => Object.values(colorBySupport), [colorBySupport]);
};
